import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef } from 'react';

import { NumberPrompt, PasswordPrompt, PromptInput, PromptInputHandle, Prompts, PromptsOptions, TextPrompt } from 'components/modal_manager/components/dialog/PromptInput';
import { Modal } from 'components/modal_manager/components/Modal';
import { Button } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';

/**
 * Options accepted by the `Action`, `OKAction`, `CancelAction`, and `SingleOKAction` factory functions.
 *
 * Note: `key` is a vestige of `react-bootstrap-dialog` and represents a keyboard shortcut binding
 * (e.g. `'enter,esc'`). Only `SingleOKAction` currently uses this field via `onSubmitPrompt`.
 */
type ActionOptions = {
    className?: string;
    key?: string;
    label?: string;
    onClick?: () => void;
    variant?: string;
};

type DialogAction = {
    className: string;
    func: () => void;
    key: string;
    label: string;
    onClick?: () => void;
    variant: string;
};

type ShowOptions = {
    actions?: DialogAction[];
    body?: ReactNode;
    centered?: boolean;
    onHide?: () => void;
    prompt?: Prompts;
    size?: string;
    title?: ReactNode;
};

type DefaultOverrides = {
    defaultButtonVariant?: string;
    defaultCancelButtonVariant?: string;
    defaultCancelLabel?: string;
    defaultOkLabel?: string;
};

type DialogProps = {
    defaultOverrides?: DefaultOverrides;
    size?: string;
};

type DialogState = {
    actions: DialogAction[];
    body: ReactNode;
    centered: boolean;
    onHide: (() => void) | null;
    prompt: Prompts | null;
    reject: (reason?: unknown) => void;
    resolve: (value: unknown) => void;
    show: boolean;
    size?: string;
    title: ReactNode;
};

export type DialogHandle = {
    Action: (options?: ActionOptions) => DialogAction;
    CancelAction: (options?: ActionOptions) => DialogAction;
    getInputValue: () => string | null;
    getSize: (defaultSize?: string) => string | null | undefined;
    hide: () => void;
    NumberPrompt: (options?: PromptsOptions) => NumberPrompt;
    OKAction: (options?: ActionOptions) => DialogAction;
    PasswordPrompt: (options?: PromptsOptions) => PasswordPrompt;
    show: (options: ShowOptions) => Promise<unknown>;
    showAlert: (body: string, size?: string) => void;
    SingleOKAction: (options?: ActionOptions) => DialogAction;
    TextPrompt: (options?: PromptsOptions) => TextPrompt;
};

/**
 * A Modal dialog that can be used in place of `window.alert()` and `window.confirm()`
 * @example
 *      const my_dialog = useRef();
 *
 *      <Dialog ref={my_dialog} />
 *
 * @example
 *  const dialog = my_dialog?.current;
 *  if (!dialog) return;
 *
 *  dialog.show({
 *      body: 'Please enter a number!',
 *      prompt: dialog.NumberPrompt({ initialValue: 0 }),
 *      actions: [
 *      dialog.CancelAction(),
 *      dialog.Action({
 *          label: 'Do something!',
 *          onClick: () => console.log("I'm doing it!"),
 *          variant: 'danger'
 *          })
 *      ],
 *      size: 'sm'
 *  })
 *
 * @example
 *      my_dialog.current.showAlert('Uh oh!', 'sm')
 */
export const Dialog = forwardRef<DialogHandle, DialogProps>((props, forwardedRef) => {

    const { defaultOverrides, size: propSize = 'normal', ...modalProps } = props;

    const [state, setState, resetState] = useSuperState<DialogState>({
        actions: [],
        body: null,
        centered: false,
        onHide: null,
        prompt: null,
        reject: () => undefined as void,
        resolve: () => undefined as void,
        show: false,
        size: propSize,
        title: null
    });

    const dialog_options = useRef<ShowOptions | undefined>(undefined);
    const prompt_input = useRef<PromptInputHandle>(null);
    const { formatMessage } = useIntl();

    const DEFAULT_OPTIONS = {
        defaultButtonVariant: 'primary',
        defaultCancelButtonVariant: 'secondary',
        defaultCancelLabel: formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' }),
        defaultOkLabel: formatMessage({ id: 'ok.981b7e92', defaultMessage: 'Ok' })
    };

    const handleShow = (options: ShowOptions) => new Promise((resolve, reject) => {

        dialog_options.current = options;
        setState({ ...options, reject, resolve, show: true });

    });

    const handleHide = () => {

        const { onHide, resolve, show } = state;

        // If the modal is already closed, do nothing
        if (!show) return;

        typeof onHide === 'function' && onHide();

        resolve(null);

        // Close the modal
        setState({ show: false }, () => setTimeout(resetState, 500));

    };

    // Factory function for generating "Action" button properties from supplied options
    const createAction = (options?: ActionOptions): DialogAction => {

        const onClick = options?.onClick;

        return {
            className: options?.className || '',
            func: () => {

                // Fire the onClick function if supplied
                onClick?.();

                // Hide the dialog
                handleHide();

            },
            key: options?.key || '',
            label: options?.label || '',
            onClick,
            variant: options?.variant || DEFAULT_OPTIONS.defaultButtonVariant
        };

    };

    // Returns the value of the related PromptInput if used
    const getInputValue = () => prompt_input.current?.getValue?.() || null;

    // When the user submits the prompt (presses Enter), trigger the first action bound to 'enter'
    const onSubmitPrompt = () => {

        const enterAction = state.actions?.find(action => action.key?.includes('enter'));
        enterAction?.func?.();

    };

    const getSize = (defaultSize?: string): string | null | undefined => {

        if (typeof state.size === 'undefined') return defaultSize;
        return state.size === 'medium' ? null : state.size;

    };

    // Generates a simple `window.alert()` style dialog containing a message and an "OK" button
    const showAlert = (body: string, size?: string) => {

        const options: ShowOptions = {
            actions: [SingleOKAction()],
            body,
            size
        };

        handleShow(options);

    };

    // Generates an action button based on the provided options
    const Action = (options?: ActionOptions) => createAction(options);

    // Generates an "OK" action button based on the provided options, with a default label applied
    const OKAction = (options?: ActionOptions) => {

        const label = options?.label || defaultOverrides?.defaultOkLabel || DEFAULT_OPTIONS.defaultOkLabel || '';
        return createAction({ ...options, label });

    };

    // Generates a "cancel" action button based on the provided options, with a default label and variant applied
    const CancelAction = (options?: ActionOptions) => {

        const label = options?.label || defaultOverrides?.defaultCancelLabel || DEFAULT_OPTIONS.defaultCancelLabel || '';
        const variant = options?.variant || defaultOverrides?.defaultCancelButtonVariant || DEFAULT_OPTIONS.defaultCancelButtonVariant || '';
        return createAction({ ...options, label, variant });

    };

    // Generates an action button bound to both Enter and Escape key shortcuts
    const SingleOKAction = (options?: ActionOptions) => {

        const label = options?.label || defaultOverrides?.defaultOkLabel || DEFAULT_OPTIONS.defaultOkLabel || '';
        return createAction({ ...options, key: 'enter,esc', label });

    };

    // Generates a text input in the body of the modal
    const generateTextPrompt = (options?: PromptsOptions) => new TextPrompt(options);

    // Generates a password input in the body of the modal
    const generatePasswordPrompt = (options?: PromptsOptions) => new PasswordPrompt(options);

    // Generates a number input in the body of the modal
    const generateNumberPrompt = (options?: PromptsOptions) => new NumberPrompt(options);

    // resetState is stable (never changes identity); empty deps intentionally runs cleanup on unmount only.
    useEffect(() => resetState, []); // eslint-disable-line react-hooks/exhaustive-deps

    useImperativeHandle(forwardedRef, () => ({
        Action,
        CancelAction,
        getInputValue,
        getSize,
        hide: handleHide,
        NumberPrompt: generateNumberPrompt,
        OKAction,
        PasswordPrompt: generatePasswordPrompt,
        show: handleShow,
        showAlert,
        SingleOKAction,
        TextPrompt: generateTextPrompt
    }));

    const { actions, body, centered, prompt, show, size, title } = state;

    return (
        <Modal {...modalProps} centered={!!centered} onHide={handleHide} show={show} size={size as 'sm' | 'lg' | 'xl' | undefined}>
            {title && (
                <Modal.Header closeButton>
                    <Modal.Title>
                        {title}
                    </Modal.Title>
                </Modal.Header>
            )}
            <Modal.Body>
                {typeof body === 'string' ? (
                    <p>
                        {body}
                    </p>
                ) : (
                    <>
                        {body}
                    </>
                )}
                {prompt && (
                    <PromptInput
                        onSubmit={onSubmitPrompt}
                        prompt={prompt}
                        ref={prompt_input}
                    />
                )}
            </Modal.Body>
            {!!actions?.length && (
                <Modal.Footer>
                    {actions.map((action, index) => (
                        <Button
                            className={`dialog-action-button ${action.className}`}
                            key={action.label || action.key || index}
                            onClick={() => action.func()}
                            size="sm"
                            variant={action.variant}
                        >
                            {action.label}
                        </Button>
                    ))}
                </Modal.Footer>
            )}
        </Modal>
    );

});

Dialog.displayName = 'Dialog';
